
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { getUserCategories } from "@/services/categories.service";
import { getCategoryOrderingForShop } from "@/services/shops.service";

interface CategoryFiltersProps {
  selectedCategories: string[];
  setSelectedCategories?: (categories: string[]) => void;
  onCategorySelect?: (category: string) => void;
  shopId?: string;
  className?: string;
  style?: React.CSSProperties;
}

const CategoryFilters = ({ 
  selectedCategories, 
  setSelectedCategories, 
  onCategorySelect,
  shopId,
  className,
  style
}: CategoryFiltersProps) => {
  const [userCategories, setUserCategories] = useState<any[]>([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getUserCategories();
        
        // If shopId is provided, get shop-specific category ordering
        if (shopId) {
          const shopCategoryOrdering = await getCategoryOrderingForShop(shopId);
          
          if (shopCategoryOrdering && shopCategoryOrdering.length > 0) {
            // Create position map from shop ordering
            const positionMap = new Map(
              shopCategoryOrdering.map(item => [item.category_id, item.position])
            );
            
            // Sort categories according to shop ordering
            const sortedCategories = [...categories]
              .map(cat => ({
                ...cat,
                position: positionMap.has(cat.id)
                  ? positionMap.get(cat.id)!
                  : cat.position ?? Number.MAX_SAFE_INTEGER
              }))
              .sort((a, b) => a.position - b.position);
            
            setUserCategories(sortedCategories);
            return;
          }
        }
        
        // If no shop or no shop ordering, use default order
        setUserCategories(categories);
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };
    
    loadCategories();
  }, [shopId]);
  
  // Handler for toggling item checked state
  const handleCategoryClick = (categoryId: string) => {
    if (onCategorySelect) {
      onCategorySelect(categoryId);
    } else if (setSelectedCategories) {
      const newSelectedCategories = selectedCategories.includes(categoryId)
        ? selectedCategories.filter(id => id !== categoryId)
        : [...selectedCategories, categoryId];
      setSelectedCategories(newSelectedCategories);
    }
  };

  return (
    <div className={`overflow-x-auto hide-scrollbar pb-1 ${className || ''}`}>
      <div className="flex gap-2">
        {userCategories.map((category) => {
          const isSelected = selectedCategories.includes(category.id);
          const bgColor = isSelected ? category.color : 'transparent';
          const textColor = isSelected ? category.textColor || '#000000' : category.color;
          const borderColor = category.color;

          return (
            <Button
              key={category.id}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryClick(category.id)}
              className="min-w-max transition-colors hover:opacity-90 focus:opacity-90"
              style={{
                backgroundColor: bgColor,
                color: textColor,
                borderColor: borderColor,
                borderWidth: '1px',
                borderStyle: 'solid',
                ...style
              }}
              
            >
              <span className="mr-1">{category.icon}</span>
              <span>{category.name}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryFilters;
